import { useState, useRef, useEffect, useCallback } from "react";
import authFetch, { setAuthToken } from "./authFetch";
import PT from "./i18n/platformTranslations";
import PlatformCtx from "./state/PlatformCtx";
import SEED from "./state/seed";
import { PLATFORM_SEED, platformReducer } from "./state/platformState";
import { usePersistedReducer } from "./state/usePersistedReducer";
import { DEFAULT_INV_CATS } from "./utils/investmentCats";
import { AnimatedSplash, LoadingSplash } from "./shared/SplashScreen";
import OfflineBanner from "./shared/OfflineBanner";
import { FACILITY_DEFAULT_PERMS, SUPPLIER_DEFAULT_PERMS } from "./permissions/checks";
import WorkspaceApp, { buildModulesFromPackage } from "./modules/shell/WorkspaceApp";
import SuperAdminPanel from "./admin/SuperAdminPanel";
import SuperAdminLogin from "./admin/SuperAdminLogin";
import LegalPage from "./platform/LegalPage";
import PlatformLanding from "./platform/PlatformLanding";
import PlatformSignup, { clearSignupSession } from "./platform/PlatformSignup";
import WorkspaceLogin from "./platform/WorkspaceLogin";
import { buildAccentGradientCss } from "./utils/accentGradient";


/* ══ PLATFORM ROOT ═════════════════════════════════════════ */
export default function App(){
  const [platform, pdispatch] = usePersistedReducer("cmd_platform", PLATFORM_SEED, platformReducer);
  const [screen, setScreen] = useState("landing");
  const [splashDone, setSplashDone] = useState(false);

  /* ── Dynamic iridescent gradient, derived from the single SA-configured
     anchor color (platform.wsDefaultAnchorColor). Same anchor that drives
     anchorDerivedColors() for the workspace palette, so the landing /
     signup / sign-in / splash / store badges / chart surfaces follow the
     workspace whenever the SA changes the anchor. One source of truth —
     no hardcoded violet→cyan outside the fallback defaults in
     command-tokens.css. Injected into a single <style id="cmd-accent-grad">
     at the document root so every .cmd-landing element picks it up. */
  useEffect(()=>{
    if(typeof document==="undefined")return;
    const anchor=platform.wsDefaultAnchorColor||platform.platformAccent||"";
    const css=buildAccentGradientCss(anchor);
    if(!css)return;
    const id="cmd-accent-grad";
    let tag=document.getElementById(id);
    if(!tag){tag=document.createElement("style");tag.id=id;document.head.appendChild(tag);}
    if(tag.textContent!==css)tag.textContent=css;
  },[platform.wsDefaultAnchorColor,platform.platformAccent]);

  /* ── Sync workspaces from DB table into platform state ──
     The cmd_platform blob may be missing or stale — authoritative
     subscription + module data lives in the workspaces table. We sync:
       1. On every app boot (retries if auth isn't ready yet)
       2. Whenever the auth token is set (post-login, post-auto-login)
       3. On window focus, so returning to a tab after an upgrade
          on another device reflects the new plan
       4. Explicitly after actions that should invalidate the cache
          (super admin login, post-signup, ?upgraded=1 redirect).
     Without these, a plan change on the server can go unreflected
     client-side indefinitely — modules stay locked even after the
     user has paid for them. */
  const syncWorkspaces=useCallback(()=>{
    authFetch("/api/list-workspaces").then(r=>{if(!r.ok)return null;return r.json();}).then(d=>{
      if(d&&d.workspaces&&d.workspaces.length>0){
        pdispatch({type:"SYNC_WORKSPACES",workspaces:d.workspaces});
      }
    }).catch(()=>{});
  },[pdispatch]);

  /* Boot sync with retry: if the token isn't ready on first paint,
     try again after a short delay so auto-login / token restore
     can complete. Bounded retries prevent hot loops. */
  useEffect(()=>{
    syncWorkspaces();
    const t1=setTimeout(syncWorkspaces,1000);
    const t2=setTimeout(syncWorkspaces,3000);
    return()=>{clearTimeout(t1);clearTimeout(t2);};
  },[syncWorkspaces]);

  /* React to auth changes (login / auto-login) by re-syncing.
     authFetch fires this event whenever setAuthToken changes the
     active token. */
  useEffect(()=>{
    const onAuthSet=()=>syncWorkspaces();
    window.addEventListener("auth:set",onAuthSet);
    return()=>window.removeEventListener("auth:set",onAuthSet);
  },[syncWorkspaces]);

  /* Re-sync when the tab regains focus so cross-device changes
     (e.g. upgrade completed on phone while desktop was idle)
     become visible without a manual reload. */
  useEffect(()=>{
    const onFocus=()=>{if(!document.hidden)syncWorkspaces();};
    window.addEventListener("focus",onFocus);
    document.addEventListener("visibilitychange",onFocus);
    return()=>{
      window.removeEventListener("focus",onFocus);
      document.removeEventListener("visibilitychange",onFocus);
    };
  },[syncWorkspaces]);
  const [activeWs, setActiveWs] = useState(null);
  const [autoLoginUser, setAutoLoginUser] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [lang, setLang] = useState("en");
  const [legalPageKey, setLegalPageKey] = useState(null);
  const [signupPkgId, setSignupPkgId] = useState(null);
  const subdomainRoutedRef = useRef(false);

  useEffect(()=>{
    // Lang from browser
    try{const l=localStorage.getItem("cmd_lang");if(l)setLang(l);}catch{}

    // Legal page routing — check URL path for /privacy, /terms, /data-deletion
    const LEGAL_ROUTES={"privacy":"privacyPolicy","terms":"termsOfService","data-deletion":"userDataDeletion","support":"supportPage"};
    const pathSeg=window.location.pathname.replace(/^\//,"").split("/")[0].toLowerCase();
    if(LEGAL_ROUTES[pathSeg]){setLegalPageKey(LEGAL_ROUTES[pathSeg]);setScreen("legal");return;}

    // Payment callback — Moyasar redirected back to the SPA after payment
    const urlP=new URLSearchParams(window.location.search);
    if(urlP.get("signup_payment")==="1"){
      const moyasarId=urlP.get("id");
      const moyasarStatus=urlP.get("status");
      if(moyasarId&&moyasarStatus==="paid"){
        /* Store payment info and go to signup screen — it will auto-resume via sessionStorage */
        try{sessionStorage.setItem("cmd_signup_paymentId",moyasarId);}catch{}
        try{sessionStorage.setItem("cmd_signup_step",JSON.stringify("creating"));}catch{}
      }
      /* Clean URL params */
      try{window.history.replaceState({},"",window.location.pathname);}catch{}
      setScreen("signup");
      return;
    }

    /* Upgrade callback — /api/upgrade-callback redirects here after a
       successful subscription change. The DB workspaces row is now updated
       with the new package_id, but the cached cmd_platform blob still has
       the old value. Force a sync so the user sees their new plan and
       module access immediately without needing a manual reload. */
    if(urlP.get("upgraded")==="1"){
      syncWorkspaces();
      try{window.history.replaceState({},"",window.location.pathname);}catch{}
    }

    // Subdomain routing — if accessed via {sub}.getcommand.app, auto-enter that workspace
    const host=window.location.hostname;
    const parts=host.split(".");
    if(parts.length>=3&&parts[0]!=="www"){
      const sub=parts[0];
      if(sub==="admin"){subdomainRoutedRef.current=true;setScreen("sa_login");return;}
      const ws=(platform.workspaces||[]).find(w=>w.subdomain===sub&&w.status==="active");
      if(ws){subdomainRoutedRef.current=true;setActiveWs(ws);setScreen("workspace");try{localStorage.setItem("cmd_active_ws",ws.id);}catch{}return;}
      // Workspace not found yet — might be loading from API. Set screen to loading.
      console.log("[subdomain] workspace '"+sub+"' not found in initial data, waiting for API hydration...");
      setScreen("loading_ws");
      return;
    }
    // Fallback: ?ws=subdomain query param routing (works without Cloudflare)
    const urlParams=new URLSearchParams(window.location.search);
    const wsParam=urlParams.get("ws");
    const isWelcome=urlParams.get("welcome")==="1";
    const signupError=urlParams.get("signup_error");
    if(wsParam){
      const ws=(platform.workspaces||[]).find(w=>w.subdomain===wsParam&&w.status==="active");
      if(ws){
        subdomainRoutedRef.current=true;setActiveWs(ws);setScreen("workspace");
        try{localStorage.setItem("cmd_active_ws",ws.id);}catch{}
        /* Clean URL params */
        try{window.history.replaceState({},"",window.location.pathname);}catch{}
        return;
      }
      /* Workspace created by payment callback but not yet in localStorage platform state —
         redirect to subdomain login after a brief wait for hydration */
      if(isWelcome){
        console.log("[welcome] workspace '"+wsParam+"' not in local state yet, will retry after hydration...");
        setScreen("loading_ws");
        return;
      }
    }
    /* Handle signup errors from payment callback redirect */
    if(signupError){
      console.error("[signup] Payment callback error:",signupError);
      try{window.history.replaceState({},"",window.location.pathname);}catch{}
    }

    // Fall back to last active workspace from localStorage
    try{
      const wsId=localStorage.getItem("cmd_active_ws");
      if(wsId){
        const ws=(platform.workspaces||[]).find(w=>w.id===wsId&&w.status==="active");
        if(ws){setActiveWs(ws);setScreen("workspace");}
      }
    }catch{}
  },[]);

  // Re-check subdomain routing after DB hydration updates platform.workspaces
  // For payment callback redirects: fetch workspace from server if not in local state
  useEffect(()=>{
    if(screen!=="loading_ws")return;
    const urlParams=new URLSearchParams(window.location.search);
    const wsParam=urlParams.get("ws");
    const isWelcome=urlParams.get("welcome")==="1";

    /* If this is a payment callback welcome redirect, try fetching workspace from server */
    if(isWelcome&&wsParam){
      (async()=>{
        try{
          const r=await authFetch(`/api/check-subscription?ws_key=cmd_ws_${wsParam}`).catch(()=>null);
          /* Even if the fetch fails, the workspace might appear in platform state via hydration */
        }catch{}
      })();
    }

    const t=setTimeout(()=>{
      if(screen==="loading_ws"){
        /* Last resort: check platform.workspaces one more time before giving up */
        const ws=wsParam?(platform.workspaces||[]).find(w=>w.subdomain===wsParam&&w.status==="active"):null;
        if(ws){setActiveWs(ws);setScreen("workspace");try{localStorage.setItem("cmd_active_ws",ws.id);}catch{}return;}
        console.log("[subdomain] timeout — workspace not found, redirecting to landing");
        setScreen("landing");
        try{window.history.replaceState({},"",window.location.pathname);}catch{}
      }
    },8000);  /* extended to 8s for payment callback hydration */
    return()=>clearTimeout(t);
  },[screen]);

  useEffect(()=>{
    if(subdomainRoutedRef.current)return;
    if(screen!=="landing"&&screen!=="loading_ws")return;
    const host=window.location.hostname;
    const parts=host.split(".");
    if(parts.length>=3&&parts[0]!=="www"){
      const sub=parts[0];
      if(sub==="admin"){subdomainRoutedRef.current=true;setScreen("sa_login");return;}
      const ws=(platform.workspaces||[]).find(w=>w.subdomain===sub&&w.status==="active");
      if(ws){subdomainRoutedRef.current=true;setActiveWs(ws);setScreen("workspace");try{localStorage.setItem("cmd_active_ws",ws.id);}catch{};return;}
    }
    const wsParam=new URLSearchParams(window.location.search).get("ws");
    if(wsParam){
      const ws=(platform.workspaces||[]).find(w=>w.subdomain===wsParam&&w.status==="active");
      if(ws){subdomainRoutedRef.current=true;setActiveWs(ws);setScreen("workspace");try{localStorage.setItem("cmd_active_ws",ws.id);}catch{};}
    }
  },[platform.workspaces, screen]);

  const switchLang=(l)=>{setLang(l);try{localStorage.setItem("cmd_lang",l);}catch{}};

  const enterWorkspace=(ws, user=null)=>{
    setAutoLoginUser(user);
    setActiveWs(ws);setScreen("workspace");
    try{localStorage.setItem("cmd_active_ws",ws.id);}catch{}
  };

  const exitWorkspace=()=>{
    setAutoLoginUser(null);
    setActiveWs(null);setScreen("landing");setImpersonating(false);
    try{localStorage.removeItem("cmd_active_ws");}catch{}
  };

  const provisionSubdomain=async(subdomain)=>{
    try{
      const r=await authFetch("/api/provision-subdomain",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({subdomain})});
      const d=await r.json();
      if(!d.ok)console.error("provision-subdomain error:",d.error,d.details);
      else console.log("provision-subdomain ok:",d.domain);
      return d;
    }catch(err){console.error("provision-subdomain failed:",err);return{ok:false,error:err.message};}
  };

  /* ── Ref to hold newly created workspace+user for "Done" screen → "Enter workspace" button ── */
  const createdWsRef=useRef(null);
  const createdUserRef=useRef(null);

  const handleSignupCreated=async(serverResult)=>{
    /*
     * serverResult comes from POST /api/signup:
     *   { ok, token, workspace, owner, householdData }  — for open/invite mode
     *   { ok, pending }                                   — for approval mode
     */
    if(serverResult.pending){
      pdispatch({type:"ADD_PENDING",signup:{
        id:`ws_${Date.now()}`,name:serverResult.workspace?.ownerName||"",email:serverResult.workspace?.ownerEmail||"",
        workspaceName:serverResult.workspace?.name||"",subdomain:serverResult.workspace?.subdomain||"",
        requestedAt:new Date().toISOString().slice(0,10),
      }});
      return;
    }

    const ws=serverResult.workspace;
    const owner=serverResult.owner;
    if(!ws)return;

    /* CRITICAL: install the JWT before any downstream API call.
       Without this, seedDB (categories) and the cmd_platform update
       below all fire unauthenticated, hit 401 in crud.js / data.js,
       and fail silently — the workspace never shows up in super
       admin (platform.workspaces never gets updated server-side) and
       the user can't add anything inside the workspace. */
    if(serverResult.token)setAuthToken(serverResult.token);
    const wsKey=ws.wsKey;

    /* ── Seed localStorage for useRealtimeSync (same as before, but workspace+user come from server) ── */
    const ownerUser={id:owner?.id||"u1",name:owner?.name||ws.ownerName,nameAr:"",initials:(owner?.name||ws.ownerName||"").slice(0,2).toUpperCase(),role:"owner",email:ws.ownerEmail,phone:ws.ownerPhone||"",active:true,assignedLoc:"",allowedCats:[],allowedLocs:[]};

    const catPrefix=wsKey.slice(-6)+"_";
    const prefixCats=(cats)=>(cats||[]).map(c=>({...c,id:catPrefix+c.id,parent:c.parent?catPrefix+c.parent:null}));
    const blankWs={
      lang:SEED.lang, currency:platform.platformCurrency||"SAR", appName:ws.name, tagline:SEED.tagline,
      branding:SEED.branding, locFilter:"", hats:SEED.hats,
      expenseCats:prefixCats(platform.platformCategories?.expense||SEED.expenseCats),
      incomeCats:prefixCats(platform.platformCategories?.income||SEED.incomeCats),
      investmentCategories:(platform.platformInvestmentConfig?.investmentCategories||DEFAULT_INV_CATS),
      staffRoles:SEED.staffRoles, staffAuthorities:{leaveApprovers:[ownerUser.id],loanApprovers:[ownerUser.id],salaryApprovers:[ownerUser.id]},
      mealCategories:(platform.platformCategories?.meal||SEED.mealCategories),
      maintenanceCategories:(platform.platformCategories?.maintenance||SEED.maintenanceCategories),
      facilityConfig:{
        issueKinds:(platform.platformFacilityConfig?.issueKinds||["issue","repair","inspection","preventive"]),
        assignableRoles:(platform.platformFacilityConfig?.assignableRoles||["owner","family_manager","staff_handyman","staff_maid","staff_driver"]),
        defaultAssigneeRole:platform.platformFacilityConfig?.defaultAssigneeRole||"",
        autoCreateTask:platform.platformFacilityConfig?.autoCreateTask!==false,
        requireApproval:platform.platformFacilityConfig?.requireApproval||false,
        requireExpense:platform.platformFacilityConfig?.requireExpense||false,
        slaDays:platform.platformFacilityConfig?.slaDays||7,
        slaWarningPercent:platform.platformFacilityConfig?.slaWarningPercent||80,
        scheduleLeadDays:platform.platformFacilityConfig?.scheduleLeadDays!=null?platform.platformFacilityConfig.scheduleLeadDays:7,
        defaultPriority:platform.platformFacilityConfig?.defaultPriority||"medium",
      },
      facilityPermissions:{...(platform.platformFacilityPermissions||FACILITY_DEFAULT_PERMS)},
      supplierPermissions:{...(platform.platformSupplierPermissions||SUPPLIER_DEFAULT_PERMS)},
      docTags:SEED.docTags||[],
      householdData:serverResult.householdData||{},
      users:[ownerUser], locations:[], projects:[],
      income:[], expenses:[], recurringIncome:[], recurringExpenses:[],
      holdings:[], investmentLiabilities:[], balanceItems:[], vehicles:[],
      vehicleMaintRecords:[], vehicleMileageLog:[],
      groceries:[], maintenance:[], maintenanceSchedules:[],
      notifications:[], calAccounts:[], meetings:[],
      staff:[], leaveRequests:[], loanRequests:[],
      meals:[], mealPreferences:[], menuCycles:[],
      suppliers:[], transportRequests:[], dashboardConfig:null,
      modules:buildModulesFromPackage(ws.packageId,platform.platformPackages), navOrder:null, notifPrefs:{},
    };
    try{localStorage.setItem(wsKey,JSON.stringify(blankWs));}catch{}
    try{
      const rtData={
        income:[], recurringIncome:[], incomeCats:blankWs.incomeCats,
        expenses:[], expenseCats:blankWs.expenseCats, recurringExpenses:[],
        holdings:[], investmentLiabilities:[], balanceItems:[], projects:[],
        staff:[], leaveRequests:[], loanRequests:[],
        users:blankWs.users, locations:[], vehicles:[],
        vehicleMaintRecords:[], vehicleMileageLog:[],
        groceries:[], maintenance:[], maintenanceSchedules:[], suppliers:[],
        transportRequests:[],
        calAccounts:[], meetings:[], meals:[], mealPreferences:[], menuCycles:[],
        notifications:[],
      };
      localStorage.setItem(`cmd_rt_${wsKey}`,JSON.stringify(rtData));
      const cfgData={
        lang:blankWs.lang, appName:blankWs.appName, tagline:blankWs.tagline,
        locFilter:"", dashboardConfig:null,
        branding:blankWs.branding, modules:blankWs.modules, navOrder:null,
        moduleSubtitles:{}, apiKeys:{}, notifPrefs:{},
        hats:blankWs.hats, staffRoles:blankWs.staffRoles, staffAuthorities:blankWs.staffAuthorities,
        mealCategories:blankWs.mealCategories, maintenanceCategories:blankWs.maintenanceCategories,
        facilityConfig:blankWs.facilityConfig, facilityPermissions:blankWs.facilityPermissions,
        supplierPermissions:blankWs.supplierPermissions,
        investmentCategories:blankWs.investmentCategories||[], docTags:blankWs.docTags||[],
      };
      localStorage.setItem(`cmd_cfg_${wsKey}`,JSON.stringify(cfgData));
    }catch{}

    /* ── Seed database categories + config (owner user already created by /api/signup) ── */
    const seedDB=async()=>{try{
      const h={"Content-Type":"application/json"};
      const promises=[];
      for(const cat of blankWs.expenseCats||[]){
        const dbRow={id:cat.id, name:cat.name, nameAr:cat.nameAr||"", parent:cat.parent||null};
        promises.push(authFetch("/api/crud",{method:"POST",headers:h,body:JSON.stringify({table:"expense_categories",ws_key:wsKey,row:dbRow})}).catch(()=>{}));
      }
      for(const cat of blankWs.incomeCats||[]){
        const dbRow={id:cat.id, name:cat.name, nameAr:cat.nameAr||"", parent:cat.parent||null};
        promises.push(authFetch("/api/crud",{method:"POST",headers:h,body:JSON.stringify({table:"income_categories",ws_key:wsKey,row:dbRow})}).catch(()=>{}));
      }
      const cfgKeys={
        lang:blankWs.lang,appName:blankWs.appName,tagline:blankWs.tagline,branding:blankWs.branding,
        modules:blankWs.modules,hats:blankWs.hats,staffRoles:blankWs.staffRoles,staffAuthorities:blankWs.staffAuthorities,
        mealCategories:blankWs.mealCategories,maintenanceCategories:blankWs.maintenanceCategories,
        facilityConfig:blankWs.facilityConfig,facilityPermissions:blankWs.facilityPermissions,
        supplierPermissions:blankWs.supplierPermissions,
        investmentCategories:blankWs.investmentCategories||[],docTags:blankWs.docTags||[],
        notifPrefs:{},
      };
      for(const[k,v]of Object.entries(cfgKeys)){
        promises.push(authFetch("/api/config",{method:"POST",headers:h,body:JSON.stringify({ws_key:wsKey,key:k,value:v})}).catch(()=>{}));
      }
      await Promise.all(promises);
    }catch(e){console.error("[seedDB]",e);}};
    await seedDB();

    /* Update platform state with the server-created workspace */
    if(ws.promoCode)pdispatch({type:"USE_PROMO_CODE",code:ws.promoCode,workspaceId:ws.id});
    pdispatch({type:"ADD_WORKSPACE",ws});

    /* Force immediate DB write — can't read localStorage here because pdispatch
       triggers an async React re-render. Build the updated state manually. */
    try{
      const currentPlatform=JSON.parse(localStorage.getItem("cmd_platform")||"{}");
      const updatedPlatform={...currentPlatform, workspaces:[...(currentPlatform.workspaces||[]), ws]};
      /* Also update promo code usage if applicable */
      if(ws.promoCode&&updatedPlatform.promoCodes){
        updatedPlatform.promoCodes=updatedPlatform.promoCodes.map(pc=>pc.code===ws.promoCode?{...pc,usedAt:new Date().toISOString().slice(0,10),workspaceId:ws.id}:pc);
      }
      /* Write to both localStorage (so usePersistedReducer picks it up) and DB */
      localStorage.setItem("cmd_platform",JSON.stringify(updatedPlatform));
      await authFetch("/api/data",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({key:"cmd_platform",value:updatedPlatform}),keepalive:true});
    }catch(e){console.error("[handleSignupCreated] immediate DB save failed:",e);}

    /* Sync from DB table as a safety net in case the blob write above failed */
    syncWorkspaces();

    /* Store refs for the "Done" screen — DO NOT enter workspace yet (fixes finding #10) */
    createdWsRef.current=ws;
    createdUserRef.current=ownerUser;
  };

  /* ── Animated splash on native cold start ── */
  const splashOverlay=!splashDone?<AnimatedSplash appName={platform.platformName||"Command"} onDone={()=>setSplashDone(true)}/>:null;
  const offlineBanner=<OfflineBanner/>;

  /* Active workspace */
  if(screen==="loading_ws"){
    return <><LoadingSplash appName={platform.platformName} tagline="Preparing your workspace"/></>;
  }

  if(screen==="workspace"&&activeWs){
    const t=PT[lang];
    const accent=platform.platformAccent||"#C9A84C";
    return(
      <div>
        {splashOverlay}
        {offlineBanner}
        {impersonating&&(
          <div className="impersonation-bar" style={{"--cgold":accent}}>
            
            <span style={{color:accent,fontWeight:600}}>⚠</span>
            <span style={{color:"var(--cg700)",fontSize:12}}>{t.impersonating} <strong>{activeWs.name}</strong></span>
            <button onClick={exitWorkspace} style={{marginInlineStart:"auto",background:accent,color:"var(--cink)",border:"none",borderRadius:6,padding:"4px 14px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'JetBrains Mono',monospace"}}>{t.exitImpersonation}</button>
          </div>
        )}
        <div style={{paddingTop:impersonating?39:0}}>
          <PlatformCtx.Provider value={platform}>
            {/* Always use the LIVE workspace record from platform.workspaces
                instead of the stale captured activeWs object. Otherwise a
                subscription upgrade (syncWorkspaces → SYNC_WORKSPACES) updates
                the blob but WorkspaceApp keeps receiving the old packageId
                via wsInfo, leaving the user on their previous plan's module set. */}
            {(() => {
              const liveWs = (platform.workspaces||[]).find(w=>w.id===activeWs.id) || activeWs;
              return <WorkspaceApp wsKey={liveWs.wsKey} wsId={liveWs.id} onImpersonationExit={impersonating?exitWorkspace:undefined} onLogoutToMain={()=>{setAutoLoginUser(null);setActiveWs(null);setScreen("login");try{localStorage.removeItem("cmd_active_ws");}catch{}}} pdispatch={pdispatch} wsInfo={liveWs} platform={platform} autoLoginUser={autoLoginUser}/>;
            })()}
          </PlatformCtx.Provider>
        </div>
      </div>
    );
  }

  if(screen==="superadmin")
    return <SuperAdminPanel platform={platform} dispatch={pdispatch} lang={lang} setLang={switchLang}
      onImpersonate={ws=>{setActiveWs(ws);setImpersonating(true);setScreen("workspace");}}
      onExit={()=>setScreen("landing")} provisionSubdomain={provisionSubdomain}/>;

  if(screen==="sa_login")
    return <SuperAdminLogin platform={platform} lang={lang} setLang={switchLang}
      onSuccess={()=>{syncWorkspaces();setScreen("superadmin");}} onBack={()=>setScreen("landing")}/>;

  if(screen==="signup")
    return <PlatformSignup platform={platform} lang={lang} setLang={switchLang} preselectedPkgId={signupPkgId}
      onCreated={handleSignupCreated} onBack={()=>{setScreen("landing");setSignupPkgId(null);clearSignupSession();}}
      onEnterWorkspace={()=>{if(createdWsRef.current)enterWorkspace(createdWsRef.current,createdUserRef.current);}}/>;

  if(screen==="login")
    return <WorkspaceLogin platform={platform} workspaces={platform.workspaces||[]} lang={lang} setLang={switchLang}
      onLogin={enterWorkspace} onBack={()=>setScreen("landing")}/>;

  if(screen==="legal"&&legalPageKey)
    return <LegalPage platform={platform} pageKey={legalPageKey} lang={lang} setLang={switchLang}
      onBack={()=>{setScreen("landing");setLegalPageKey(null);window.history.pushState({},"","/");}}/>;

  /* Landing */
  return(
    <>
    {splashOverlay}
    <PlatformLanding platform={platform} lang={lang} setLang={switchLang}
      onSignup={(pkgId)=>{if(platform.signupMode==="disabled")return;setSignupPkgId(pkgId||null);setScreen("signup");}} onLogin={()=>setScreen("login")}
      onAdmin={()=>setScreen("sa_login")}
      onLegal={(key)=>{setLegalPageKey(key);setScreen("legal");window.scrollTo(0,0);window.history.pushState({},"","/"+({privacyPolicy:"privacy",termsOfService:"terms",userDataDeletion:"data-deletion",supportPage:"support"}[key]||"privacy"));}}/>
    </>
  );
}
